'use client';
import { clsx } from 'clsx';
import NextImage from 'next/image';
import { FallbackImage } from 'components/UI/FallbackImage';
import type { PropsWithClassName } from 'types/react-props';
import { getFilename, normalizeBaseUrl } from 'utils/image';
import { getImageLoader } from 'utils/imageLoader';
import styles from './Image.module.scss';

const imageSizes = [124, 240, 360, 480, 768, 1024, 1200];

const cropDefault = 'c16_9';

export type AllowedCropTypes = 'c2_1' | 'c4_3' | 'c3_4' | 'c1_1' | 'c21_9' | 'original' | typeof cropDefault;

export type ApiImageProps = {
  readonly id: string;
  readonly baseUrl: string;
  readonly crop: AllowedCropTypes;
  readonly defaultUrl: string;
  readonly filename: string;
  readonly originalWidth: number;
  readonly originalHeight: number;
  readonly fields?: {
    readonly alt?: {
      readonly value: string;
    };
  };
  readonly isExtern?: boolean;
};

export type ImageProps = PropsWithClassName<{
  width?: number;
  alt?: string;
  title?: string;
  readonly image?: ApiImageProps;
  src?: string;
  fallback?: {
    className: string;
  };
  mimeType?: string;
  priority?: boolean;
  cropOverride?: AllowedCropTypes;
  sizes?: string;
  decoding?: 'async' | 'sync' | 'auto';
  maxHeight?: number;
}>;

const getSizes = (width: number) => {
  return imageSizes
    .filter((point) => point <= width)
    .map((imageSize) => `(max-width: ${imageSize}px) ${imageSize}px`)
    .concat(`${width} px`)
    .join(', ');
};

const getDimensions = (crop: string, width = 870, originalRatio?: number) => {
  const aspectRatio = crop === 'original' ? (originalRatio ?? getAspectRatioValue(crop)) : getAspectRatioValue(crop);

  return {
    imageWidth: width,
    imageHeight: Math.round(width / aspectRatio),
  };
};

const getAspectRatioValue = (crop: string) => {
  const [width, height] = crop
    .replace('c', '')
    .split('_')
    .map((x) => parseInt(x, 10));

  return width / height;
};

const createOptimizedImage = ({
  baseUrl,
  fileName,
  crop,
  width,
  className,
  title,
  mimeType,
  priority,
  sizes,
  alt,
  decoding,
  originalRatio,
  maxHeight,
}: {
  baseUrl: string;
  fileName: string;
  crop: AllowedCropTypes;
  width: number;
  className: string;
  alt?: string;
  title?: string;
  priority?: boolean;
  mimeType?: string;
  sizes?: string;
  decoding?: 'async' | 'sync' | 'auto';
  originalRatio?: number;
  maxHeight?: number;
}) => {
  const { imageWidth, imageHeight } = getDimensions(crop, width, originalRatio);

  const { loader, unoptimized } = getImageLoader(baseUrl, fileName, crop, mimeType, maxHeight);

  return (
    <NextImage
      loader={loader}
      alt={alt ?? fileName}
      unoptimized={unoptimized}
      className={className}
      height={imageHeight}
      width={imageWidth}
      src={baseUrl}
      sizes={sizes ?? getSizes(imageWidth)}
      title={title}
      priority={priority}
      suppressHydrationWarning
      decoding={decoding}
      fetchPriority={priority ? 'high' : 'auto'}
    />
  );
};

const Image = (props: ImageProps) => {
  if (props?.image?.isExtern && props?.image?.defaultUrl) {
    return (
      /* eslint-disable-next-line @next/next/no-img-element */
      <img
        src={props?.image?.defaultUrl}
        alt={`Bild zu: ${props?.alt ?? ''}`}
        className={clsx(props.className, styles.image)}
      />
    );
  }
  const { alt, width, className, title, priority, cropOverride, sizes, decoding = 'async', image, maxHeight } = props;
  const originalRatio = image ? image.originalWidth / image.originalHeight : undefined;

  if (!props.image) {
    if (props.src && width) {
      return createOptimizedImage({
        baseUrl: props.src,
        fileName: 'image',
        crop: cropOverride ?? 'c16_9',
        width,
        className: clsx(styles.image, className),
        title,
        priority,
        sizes,
        alt,
        decoding,
        originalRatio,
        maxHeight,
      });
    }

    return <FallbackImage className={props.fallback?.className} />;
  }

  const { baseUrl, filename, crop = cropDefault, originalWidth } = props.image;
  const usedWidth = width ?? originalWidth;

  return createOptimizedImage({
    baseUrl: normalizeBaseUrl(baseUrl),
    fileName: getFilename(filename, alt),
    crop: cropOverride ?? crop,
    width: usedWidth,
    className: clsx(styles.image, className),
    title,
    priority,
    ...(props.mimeType ? { mimeType: props.mimeType } : null),
    sizes,
    alt,
    decoding,
    originalRatio,
    maxHeight,
  });
};

export default Image;
